import React, { useReducer, useState, useEffect, useRef, useMemo } from 'react'
import getConfig from 'next/config'
import { setCookie, parseCookies } from 'nookies'
import MarketingHelper from '@zuto-internal/zuto-marketing-helper'
import { GridItem } from '@zuto/core/src/grid'
import { ProgressBar, PrivacyNotice, Loading } from '@zuto/molecules'
import $console from '@zuto/utilities/src/$console'
import { useCallbackOnce, useDidMountEffect } from '@zuto/utilities'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import { useDetectAdBlock } from 'adblock-detect-react'
import { useRouter } from 'next/router'
import QuestionUtils from '../../Questions/question-utils'
import { ApplicationFormContext, Action, ApplicationFormState } from './ApplicationFormContext'
import mapApplication, { Application } from '../../Mappers/ApplicationFormMapper'
import SubmissionService from '../../services/submission-service'
import EventsService from '../../services/events-service'
import Thankyou from '../Thankyou'
import ErrorModal from '../ErrorModal'
import CannotProceed from '../CannotProceed'
import VehicleIntroLargerImageNoDropdown from '../Introductions/VehicleIntroduction/VehicleIntroLargerImageNoDropdown'
import DefaultIntroduction from '../Introductions/DefaultIntroduction'
import RepresentativeExample from '../RepresentativeExample'
import { YEAR } from '../../constants'
import { isIOSDevice } from '../../utils/detect-device'
import ResumeForm from '../ResumeForm'
import { fireReactGAEvent, fireReactGAEventThen } from '../Questions/utils'
import { ApplicationFormProps } from './types'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../utils/amplitude-utils'
import { mapAnswers } from '../../utils/ApplicationForm-utils'
import { zaSubmissionError } from '../../utils/monitoring-utils/monitoring-utils'
import ThemeButton from '../AdditionalInformation/ZutoUi/ThemeButton'
import { Box, Grid, styled, Typography } from '@mui/material'
import Heading from '../AdditionalInformation/ZutoUi/Heading'
import SixCardGrid from '../AdditionalInformation/ZutoUi/CardGrid-6'
import MuiAccordion from '../AdditionalInformation/ZutoUi/Accordion'
import IconTileGridFour from '../AdditionalInformation/ZutoUi/IconTileGrid-4'
import { faqAccordionItems, howZutoWorksTiles, whyChooseZutoCards } from '../AdditionalInformation/AdditionalInformationContent'
import { tonalPalettes } from '@zuto/theme/src/mui/Colors'
import { space } from '@zuto/theme/src/mui/Core'
import { TypographyColorOptions } from '../AdditionalInformation/ZutoUi/Ui'
import { sendFormAbandonmentEvent } from '../../utils/monitoring-utils/sendFormAbandonmentEvent'
import VehicleHeader from '../VehicleHeader/VehicleHeader'
import customCSS from '../../style/colorVariationV1'
import TrustBox from '../TrustPilotHeader/TrustBox'
import { addVehicleToJoinZutoRedirect } from '../../utils/url-utils'
import ConflictErrorModalContent from '../ErrorModal/ConflictErrorModalContent'

// TODO: Add tests
export const ApplicationForm = ({
  config,
  vehicle: vehicleProp,
  source,
  prefillData,
  lenderDetails,
  showRepRate,
  eventsServiceUrl,
  customerId,
  experimentName,
  isOfficeTraffic,
  isBotTraffic,
  journey,
  featureSwitches
}: ApplicationFormProps): JSX.Element => {
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()
  const sourceName = source?.name // different to sourceDisplayName - eg sourceDisplayName could be freedomfinance (utm_source) and sourceDisplayName is Aro (after their light touch rebrand)
  const sourceDisplayName = source?.displayName
  const { publicRuntimeConfig } = getConfig()
  const { cookieDomain } = publicRuntimeConfig
  const [vehicle] = useState(vehicleProp)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [conflictError, setConflictError] = useState(false)
  const [isDirty, setDirty] = useState(false)
  const [applicationFormCb, resetApplicationFormCb] = useCallbackOnce()
  const [loadedOnce, setLoadedOnce] = useState(false)
  const { Device: deviceId, Session: sessionId, zuto_wbraid: wbraid, zuto_gbraid: gbraid, zuto_fbclid: fbclid } = parseCookies()
  let adBlockDetected = true
  adBlockDetected = useDetectAdBlock()
  const { progressBarWithCar, resumeV1, digiLandingPageV1, atLandingPageV6, vehicleImgV1, colorVariationV1, msmThemeV1 } = featureSwitches

  type ThemeButtonProps = {
    $atLandingPageV6?: boolean
  }

  const LandingPageInformationWrapper = styled('div')(({ theme }) => ({
    marginTop: 40,
    [theme.breakpoints.up('lg')]: {
      marginTop: 60
    }
  }))

  const ThemeButtonWrapper = styled('div')<ThemeButtonProps>(({ theme, $atLandingPageV6 }) => ({
    maxWidth: '70.5rem',
    margin: '0 auto',
    display: 'flex',
    ...($atLandingPageV6 && {
      position: 'sticky',
      bottom: 0,
      background: tonalPalettes.secondary[99],
      padding: `${theme.spacing(6)} ${theme.spacing(5)}`,
      display: 'block',
      [theme.breakpoints.up('md')]: {
        display: 'none'
      }
    }),
    div: {
      marginLeft: space.spacing[7],
      marginRight: space.spacing[7],
      width: '100%',
      [theme.breakpoints.up('md')]: {
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: '461px'
      },
      ...($atLandingPageV6 && {
        margin: 0,
        width: 'unset'
      })
    }
  }))

  // Memoised so each question gets the same params (and events service) until one of these values changes
  const appFormEventParams = useMemo(() => ({
    Lender: lenderDetails?.lenderName,
    PreApproved: lenderDetails?.preapproved,
    QuoteId: lenderDetails?.quoteId,
    CustomerId: customerId,
    PartnerCustomerId: prefillData?.partnerCustomerId,
    Prefill: !!prefillData,
    DeviceId: deviceId,
    SessionId: sessionId,
    Experiments: experimentName,
    Journey: journey
  }), [
    lenderDetails?.lenderName,
    lenderDetails?.preapproved,
    lenderDetails?.quoteId,
    customerId,
    prefillData?.partnerCustomerId,
    !!prefillData,
    deviceId,
    sessionId,
    experimentName,
    journey
  ])

  const appFormMetadata = {
    deviceId: appFormEventParams.DeviceId,
    sessionId: appFormEventParams.SessionId,
    customerId: appFormEventParams.CustomerId,
    partnerCustomerId: appFormEventParams.PartnerCustomerId,
    lender: appFormEventParams.Lender,
    isPrefill: appFormEventParams.Prefill,
    isPreapproved: appFormEventParams.PreApproved,
    isBotTraffic,
    isOfficeTraffic,
    journey,
    experiments: experimentName,
    wbraid,
    gbraid,
    fbclid
  }

  const eventsService = useMemo(() => EventsService(eventsServiceUrl, appFormEventParams), [eventsServiceUrl, appFormEventParams])

  const isRetry = useRef(false)

  const resetUnloadEvents = () => {
    global.onbeforeunload = null
    global.onpagehide = null
  }

  // Price Comparison would typically be prefill, however may be NonPrefill if the prefill data has expired (eg after 6hrs on Prospective Applicants API)
  const isPriceComparisonNonPrefillJourney =
    sourceName === 'clearscore' || sourceName === 'totallymoney' || sourceName === 'moneysupermarket' || sourceName === 'msm' || sourceName === 'clearscorerefinance' || sourceName === 'freedomfinance-t1'

  if (isPriceComparisonNonPrefillJourney) {
    prefillData = undefined
  }

  const submitApplication = (application: Application) => {
    setApiError(false)
    setConflictError(false)
    setLoading(true)
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Submit',
      label: 'FormSubmitted',
      lenderName: prefillData ? lenderDetails?.lenderName || 'zuto' : 'none'
    })

    SubmissionService(publicRuntimeConfig)
      .submitApplication(application)
      .then((response) => {
        // fire-and-forget telemetry (don’t block redirect)
        void eventsService.sendAppFormSubmittedEvent(true, isRetry.current).catch((err) => $console.error('sendAppFormStartedEvent failed', err))
        trackAmplitudeEvent(AmplitudeEvents.ApplicationSubmitted, { success: true, is_retry: isRetry.current })

        resetUnloadEvents()

        const vehicleType = application.generalDetailsField.vehicleTypeField
        const finalRedirectUrl = addVehicleToJoinZutoRedirect(response.redirectUrl, vehicleType)

        // Redirect once GTM has sent the Success hit, otherwise it can be lost when the page unloads
        fireReactGAEventThen({
          category: 'Application Form',
          action: 'Submit',
          label: 'Success'
        }, () => window.location.assign(finalRedirectUrl))
      })
      .catch((err) => {
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Submit',
          label: 'Unsuccessful',
          lenderName: prefillData ? lenderDetails?.lenderName || 'zuto' : 'none'
        })
        // fire-and-forget telemetry (don’t block redirect)
        void eventsService.sendAppFormSubmittedEvent(false, isRetry.current).catch((err) => $console.error('sendAppFormStartedEvent failed', err))
        trackAmplitudeEvent(AmplitudeEvents.ApplicationSubmitted, { success: false, is_retry: isRetry.current })

        isRetry.current = true
        $console.error('Failed to submit application')
        zaSubmissionError(experimentName, err, application.applicantField?.[0])
        if (err && err.response && err.response.status === 409) {
          setConflictError(true)
        } else {
          setApiError(true)
        }
        // Only allow another submit after a failure. On success we stay loading until the redirect,
        // which can wait for GTM, so the button can't be pressed again in the meantime.
        setLoading(false)
        resetApplicationFormCb()
      })
      .finally(() => {
        setCookie(null, 'emailAddress', application.applicantField[0].basicDetailsField.emailField, { maxAge: YEAR * 7, path: '/', domain: cookieDomain })
      })
  }

  const closeError = () => {
    setApiError(false)
    setConflictError(false)
    const submitButton = document.querySelector("button[name='submit-button']")
    if (submitButton) (submitButton as HTMLElement).focus()
  }

  const isAutotrader = sourceName === 'autotrader'
  const isRefinanceJourney = !!featureSwitches.refinanceJourneyV1

  const configLength = isAutotrader ? config.length + 1 : config.length

  const getQuestionKey = (currentIndex: number) => (isAutotrader ? config[currentIndex - 1] : config[currentIndex])

  const getQuestionName = (questionIndex: number) => {
    if (questionIndex === configLength) return 'thankYou'
    if (isAutotrader && questionIndex === 0) return 'introduction'

    return getQuestionKey(questionIndex)
  }

  const getEventLabel = (currentIndex: number, navIndex: number) => {
    const currentQuestion = getQuestionName(currentIndex)
    const navigatingTo = getQuestionName(navIndex)

    return `${currentQuestion} -> ${navigatingTo}`
  }

  const reducer = (state: ApplicationFormState, action: Action): ApplicationFormState => {
    switch (action.type) {
      case 'QUESTION_ANSWERED':
        if (action.name !== getQuestionName(state.currentQuestionIndex)) {
          // Ignore an answer for a question that's no longer on screen. E.g. a double tap on marital status sends two answers;
          // the first moves the form on to date of birth, so the second must not move it on again (that would skip date of birth).
          return state
        }
        if (state.currentQuestionIndex < configLength) {
          fireReactGAEvent({
            category: 'Application Form',
            action: 'Next Question',
            label: getEventLabel(state.currentQuestionIndex, state.currentQuestionIndex + 1),
            nonInteraction: false
          })
          return {
            ...state,
            currentQuestionIndex: state.currentQuestionIndex + 1,
            answers: {
              ...state.answers,
              [action.name]: action.value
            },
            furthestQuestionIndex: state.currentQuestionIndex + 1 > state.furthestQuestionIndex ? state.currentQuestionIndex + 1 : state.furthestQuestionIndex,
            lastNavigationAction: 'CONTINUE',
            allErrors: {
              ...state.allErrors,
              [action.name]: { ...action.errors }
            },
            // empty string ('') will evaluate as a falsy value, hence why evaluating action.bouncerError !== undefined before &&
            // this will allow to clear the bouncerError when action.bouncerError === ''
            ...(action.bouncerError !== undefined && {
              bouncerError: action.bouncerError
            })
          }
        }
        return {
          ...state,
          allErrors: {
            ...state.allErrors,
            [action.name]: { ...action.errors }
          }
        }
      case 'SAVE':
        return {
          ...state,
          answers: {
            ...state.answers,
            [action.name]: action.value
          },
          allErrors: {
            ...state.allErrors,
            [action.name]: { ...action.errors }
          },
          ...(action.bouncerError !== undefined && {
            bouncerError: action.bouncerError
          })
        }
      case 'PREVIOUS_QUESTION':
        if (state.currentQuestionIndex > 0) {
          fireReactGAEvent({
            category: 'Application Form',
            action: 'Previous Question',
            label: getEventLabel(state.currentQuestionIndex, state.currentQuestionIndex - 1),
            nonInteraction: false
          })
          return {
            ...state,
            currentQuestionIndex: state.currentQuestionIndex - 1,
            lastNavigationAction: 'PREVIOUS'
          }
        }
        return state
      case 'SUBMIT': {
        const channelAttribution = MarketingHelper.getChannelAttribution()
        const mappedAnswers = mapAnswers(state.answers, featureSwitches)
        const result: Application = mapApplication(
          mappedAnswers,
          channelAttribution,
          experimentName,
          action.marketingPreferences,
          {
            ...appFormMetadata,
            isRetry: isRetry.current
          },
          vehicle,
          lenderDetails?.quoteId
        )
        applicationFormCb(() => submitApplication(result))
        return state
      }
      case 'CAN_PROCEED': {
        return {
          ...state,
          canProceed: action.value
        }
      }
      case 'SET_POST_CODE': {
        return {
          ...state,
          postalCode: action.value
        }
      }
      case 'SET_MARKETING_PREFERENCES': {
        return {
          ...state,
          marketingPreferences: {
            ...state.marketingPreferences,
            ...action.value
          }
        }
      }
      case 'NAVIGATED_FROM_QUESTION': {
        return {
          ...state,
          questionName: action.questionName,
          lastNavAction: action.lastNavAction
        }
      }
      default:
        return state
    }
  }

  const initialState: ApplicationFormState = {
    currentQuestionIndex: 0,
    answers: prefillData || {},
    furthestQuestionIndex: 0,
    canProceed: true,
    postalCode: '',
    marketingPreferences: { email: false, sms: false },
    allErrors: {},
    bouncerError: undefined
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  // The 'Current Question index' is 0 for Autotrader Landing Page, but also 0 for the First question across the other channels e.g. Digital
  const isAtLpFirstIndex = state?.currentQuestionIndex === 0

  const renderProgressBarBeforeIntro = isAutotrader

  const renderRepresentativeExample = () => {
    if (state.currentQuestionIndex === 0 && showRepRate) {
      return <RepresentativeExample sourceName={source?.name} />
    }
    return null
  }

  const renderIntroduction = () => {
    if (!state.canProceed || state.currentQuestionIndex > 0) {
      return null
    }

    if (state.currentQuestionIndex !== 0) {
      if ((vehicle && isAutotrader) || isAutotrader) {
        return (
          <VehicleIntroLargerImageNoDropdown
            vehicle={vehicle}
            isClassifiedSource={isAutotrader}
            sourceDisplayName={sourceDisplayName}
          />
        )
      }
    }

    if (state.currentQuestionIndex === 0) {
      if (isRefinanceJourney) {
        return null
      }

      if ((vehicle && isAutotrader) || isAutotrader) {
        return (
          <VehicleIntroLargerImageNoDropdown
            vehicle={vehicle}
            isClassifiedSource={isAutotrader}
            sourceDisplayName={sourceDisplayName}
          />
        )
      }

      return (
        <>
          <DefaultIntroduction
            digiLandingPageV1={digiLandingPageV1}
            isInMsmExperiment={msmThemeV1}
          />
          {renderRepresentativeExample()}
        </>
      )
    }

    return null
  }

  const renderVehicleImageV1 = () => {
    if (state.currentQuestionIndex !== 0 && vehicle && isAutotrader && state.currentQuestionIndex !== configLength) {
      return <VehicleHeader vehicle={vehicle} />
    }
    return null
  }

  const renderQuestion = () => {
    if (!state.canProceed) {
      return <CannotProceed />
    }

    if (state.currentQuestionIndex === configLength) {
      return (
        <Thankyou
          questionKey="thankYou"
          disablePrevious={state.currentQuestionIndex === 0}
          sourceDisplayName={sourceDisplayName}
        />
      )
    }

    if (isAutotrader && state.currentQuestionIndex === 0) {
      return null
    }

    const questionKey = getQuestionKey(state.currentQuestionIndex)
    const existingAnswer = state.answers[questionKey]
    const errors = state.allErrors[questionKey]
    const bouncerError = state.bouncerError

    return QuestionUtils.getQuestion(questionKey, existingAnswer, state.currentQuestionIndex, state.lastNavigationAction === 'PREVIOUS', errors, bouncerError, eventsServiceUrl, appFormEventParams)
  }

  const renderProgressBar = () => {
    let currentIndex = state.currentQuestionIndex
    let amountOfQuestions = configLength

    if (isAutotrader) {
      currentIndex += 1
      amountOfQuestions += 1
    }

    return (
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        pt={progressBarWithCar ? '10px' : ''}
      >
        <ProgressBar
          decimal={amountOfQuestions > 0 ? currentIndex / amountOfQuestions : 0}
          showCar={progressBarWithCar}
          currentQuestionIndex={currentIndex}
          amountOfQuestions={amountOfQuestions}
        />
      </GridItem>
    )
  }

  const handleAtLpStartApplicationButtonClick = () => {
    dispatch({
      type: 'QUESTION_ANSWERED',
      name: 'introduction',
      value: ''
    })
  }

  const renderInfoUnderAppForm = () => {
    return (
      <LandingPageInformationWrapper>
        <IconTileGridFour
          tile1={howZutoWorksTiles.tile1}
          tile2={howZutoWorksTiles.tile2}
          tile3={howZutoWorksTiles.tile3}
          tile4={howZutoWorksTiles.tile4}
          spacingBottomDesktop="40px"
          spacingBottomMobile="20px"
        />
        <ThemeButtonWrapper>
          <ThemeButton
            content="Ok, let's go"
            idSuffix="how-zuto-works"
            variant="contained"
            handleOnClick={handleAtLpStartApplicationButtonClick}
            spacingBottomDesktop="60px"
            spacingBottomMobile="40px"
          />
        </ThemeButtonWrapper>
        <Heading
          variant="headlineMedium"
          domElement="h2"
          text="Why choose Zuto"
          align="center"
          color={tonalPalettes.neutral[40] as TypographyColorOptions}
        />
        <SixCardGrid
          card1={whyChooseZutoCards[0]}
          card2={whyChooseZutoCards[1]}
          card3={whyChooseZutoCards[2]}
          card4={whyChooseZutoCards[3]}
          card5={whyChooseZutoCards[4]}
          card6={whyChooseZutoCards[5]}
          spacingBottomDesktop="40px"
          spacingBottomMobile="20px"
        />
        <ThemeButtonWrapper>
          <ThemeButton
            content="Ok, let's go"
            idSuffix="why-choose-zuto"
            variant="contained"
            handleOnClick={handleAtLpStartApplicationButtonClick}
            spacingBottomDesktop="60px"
            spacingBottomMobile="40px"
          />
        </ThemeButtonWrapper>
        <Heading
          variant="headlineMedium"
          domElement="h2"
          text="Frequently asked questions"
          align="center"
          color={tonalPalettes.neutral[40] as TypographyColorOptions}
        />
        <MuiAccordion
          items={faqAccordionItems}
          domElement="h3"
          spacingBottomMobile="20px"
          spacingBottomDesktop="40px"
        />
        <PrivacyNotice />
      </LandingPageInformationWrapper>
    )
  }

  const renderStickyStartApplicationButton = () => {
    return (
      <ThemeButtonWrapper $atLandingPageV6={atLandingPageV6}>
        <ThemeButton
          content="Ok, let's go"
          idSuffix="sticky-bottom"
          variant="contained"
          handleOnClick={handleAtLpStartApplicationButtonClick}
          spacingBottomDesktop="60px"
          spacingBottomMobile="40px"
        />
      </ThemeButtonWrapper>
    )
  }

  // updates the ref after the component renders
  const currentQuestionIndexRef = useRef(state.currentQuestionIndex)
  useEffect(() => {
    currentQuestionIndexRef.current = state.currentQuestionIndex
  }, [state.currentQuestionIndex])

  const applicationFormIsDirty = () => {
    global.onbeforeunload = () => {
      sendFormAbandonmentEvent({
        eventType: 'beforeUnloadDialogRendered',
        questionName: getQuestionName(currentQuestionIndexRef.current),
        deviceId,
        sessionId,
        experimentName
      })
      // returning true triggers a browser confirmation dialog
      // we use this to prevent accidental navigation away
      return true
    }

    // Detect for abandonment.
    global.onpagehide = () => {
      sendFormAbandonmentEvent({
        eventType: 'formAbandonment',
        questionName: getQuestionName(currentQuestionIndexRef.current),
        deviceId,
        sessionId,
        experimentName
      })
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Form Abandonment',
        label: getQuestionName(currentQuestionIndexRef.current)
      })
    }
  }

  useEffect(() => {
    if (colorVariationV1) {
      // Append the style element to the head of the document
      const styleElement = document.createElement('style')
      styleElement.textContent = customCSS
      document.head.appendChild(styleElement)
    }
  }, [])

  useEffect(() => {
    // fire-and-forget telemetry
    void eventsService.sendAppFormStartedEvent().catch((err) => $console.error('sendAppFormStartedEvent failed', err))
    trackAmplitudeEvent(AmplitudeEvents.ApplicationStarted)
    if (adBlockDetected) {
      try {
        zutoAnalyticsHelper.sendCustomEvent({
          eventType: 'TrustPilotDidNotLoad'
        })
      } catch (e) {
        $console.error('zutoAnalyticsHelper.sendCustomEvent error', e)
      }
    }
  }, [eventsServiceUrl])

  useEffect(() => {
    if (!isDirty) {
      applicationFormIsDirty()
      setDirty(true)
    }
  }, [state.answers])

  useDidMountEffect(() => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Form Progression',
      label: getQuestionName(state.furthestQuestionIndex)
    })
  }, [state.furthestQuestionIndex])

  const router = useRouter()
  const isIOS = isIOSDevice()

  const url = new URL(window.location.href)
  const { href } = url
  const shouldRenderResumePage = !!router?.query.resume && isIOS && loadedOnce && resumeV1
  const shouldRunSafetyNetIOS = isIOS && resumeV1 && !loadedOnce

  const cleanUrl = async () => {
    const { pathname, query } = router
    delete router.query.resume
    router.replace({ pathname, query }, undefined, { shallow: true })
    url.searchParams.delete('resume')
    window.history.replaceState({}, '', href)
  }

  const initAppIOS = async () => {
    cleanUrl()
    const { asPath, query } = router
    const baseUrl = asPath.split('?')
    await router?.replace({
      pathname: `${baseUrl[0]}`,
      query: { ...query, resume: true }
    })
    await url.searchParams.delete('resume')
    await router?.push(asPath)
    setLoadedOnce(true)
  }

  useEffect(() => {
    if (shouldRunSafetyNetIOS && isDirty) initAppIOS()
    if (shouldRunSafetyNetIOS && !isDirty) cleanUrl()
  }, [isDirty])

  if (shouldRenderResumePage) {
    const questionKey = getQuestionKey(state.currentQuestionIndex)

    return (
      <ApplicationFormContext.Provider
        value={{
          dispatch,
          getAppForm: () => state,
          featureSwitches,
          eventsService
        }}
      >
        <ResumeForm questionKey={questionKey} />
        <GridItem
          gridColumn={['auto / span 12', '3 / span 8']}
          gridRow={['auto', '6 / auto']}
          mt={0}
        >
          <PrivacyNotice />
        </GridItem>
      </ApplicationFormContext.Provider>
    )
  }

  const shouldRenderAtLandingPage = atLandingPageV6 && isAtLpFirstIndex
  const isNotInAtLandPageV6Experiment = !atLandingPageV6
  const isInAtLandingPageV6NotOnLandingPage = atLandingPageV6 && !isAtLpFirstIndex
  const shouldRenderPrivacyNotice = isInAtLandingPageV6NotOnLandingPage || isNotInAtLandPageV6Experiment

  return (
    <ApplicationFormContext.Provider
      value={{
        dispatch,
        getAppForm: () => state,
        featureSwitches,
        eventsService
      }}
    >
      <div
        className="application-form-wrapper"
        data-experiment={experimentName}
      >
        {renderProgressBarBeforeIntro ? (
          <>
            {renderProgressBar()}
            {renderIntroduction()}
          </>
        ) : (
          <>
            {renderIntroduction()}
            {renderProgressBar()}
          </>
        )}

        {vehicleImgV1 && renderVehicleImageV1()}

        {renderQuestion()}
        <GridItem
          gridColumn={['auto / span 12', '3 / span 8']}
          gridRow={['auto', '6 / auto']}
          mt={0}
        >
          {msmThemeV1 && (
            <>
              {state.currentQuestionIndex === 0 && (
                <Box textAlign="center">
                  <RepresentativeExample sourceName={source?.name} />
                </Box>
              )}
              <Box
                mb={1}
                mt={state.currentQuestionIndex === 0 ? 6 : 10}
                textAlign="center"
              >
                <Typography
                  variant="bodyLarge"
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    textAlign: 'center'
                  }}
                >
                  Customers love Zuto&apos;s service
                </Typography>
              </Box>
              <Grid
                mt={4}
                mb={4}
                style={{ textAlign: 'center' }}
              >
                <TrustBox />
              </Grid>
            </>
          )}
          {shouldRenderPrivacyNotice && (
            <PrivacyNotice
              isInMSMExperiment={msmThemeV1}
            />
          )}
        </GridItem>
        {loading && <Loading />}
        {apiError && (
          <ErrorModal
            titleText="Sorry"
            ctaText="Please try again"
            onClick={closeError}
          >
            There was a problem submitting your application
          </ErrorModal>
        )}
        {conflictError && (
          <ErrorModal
            ctaText="Close"
            onClick={closeError}
          >
            <ConflictErrorModalContent />
          </ErrorModal>
        )}
      </div>
      {shouldRenderAtLandingPage && (
        <>
          {renderInfoUnderAppForm()}
          {renderStickyStartApplicationButton()}
        </>
      )}
    </ApplicationFormContext.Provider>
  )
}
